import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/seo/SEOHead';
import portfolioHero from '../../assets/portfolio_hero.jpg';

export default function Portfolio() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://neurasites.ai';
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    "name": "NeuraSites AI Portfolio",
    "description": "Showcase of successful AI-powered website projects and case studies",
    "creator": {
      "@type": "Organization",
      "name": "NeuraSites AI",
      "url": siteUrl
    },
    "workExample": [
      {
        "@type": "WebSite",
        "name": "TechStart SaaS Platform",
        "description": "A comprehensive SaaS platform for project management with AI-powered analytics",
        "url": `${siteUrl}/portfolio`
      },
      {
        "@type": "WebSite", 
        "name": "GreenLeaf E-commerce Store",
        "description": "Organic products e-commerce platform with subscription management",
        "url": `${siteUrl}/portfolio`
      }
    ]
  };

  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', 'Local Service', 'Healthcare', 'Trades', 'Professional Services'];

  const projects = [
    {
      id: 1,
      title: 'Featured Case Study: AI Launch in Action',
      category: 'Local Service',
      client: 'Healthcare Local Business',
      description: 'Full transformation from an outdated site to a modern, fast lead‑generation machine.',
      caseStudy:
        'We rebuilt the homepage first, then rolled the new branding across the rest of the site. This is the exact outcome clients get with the AI Launch package.',
      technologies: ['AI Launch', 'Responsive Design', 'Performance Optimization'],
      results: {
        metric1: 'Modern, professional design',
        metric2: 'Mobile‑first layout',
        metric3: 'Faster load times'
      },
      image: portfolioHero,
      url: '/contact'
    },
    {
      id: 2,
      title: 'Boise Dental Practice Redesign',
      category: 'Healthcare',
      client: 'Local Business Client',
      description: 'Clean, trustworthy redesign to convert more patient inquiries.',
      caseStudy: 'Before: outdated layout. After: modern design with clear calls‑to‑action.',
      technologies: ['AI Launch', 'Copywriting', 'SEO Foundation'],
      results: {
        metric1: 'Stronger trust signals',
        metric2: 'Clearer CTAs',
        metric3: 'Improved readability'
      },
      image: portfolioHero,
      url: '/contact'
    },
    {
      id: 3,
      title: 'Treasure Valley Trade Services Site',
      category: 'Trades',
      client: 'Local Business Client',
      description: 'Rugged, dependable look with fast contact flow.',
      caseStudy: 'Focused on phone‑first leads and simple navigation.',
      technologies: ['AI Launch', 'Lead Capture', 'Speed Optimization'],
      results: {
        metric1: 'Simplified navigation',
        metric2: 'Phone‑first layout',
        metric3: 'Fast load times'
      },
      image: portfolioHero,
      url: '/contact'
    },
    {
      id: 4,
      title: 'Professional Services Authority Site',
      category: 'Professional Services',
      client: 'Local Business Client',
      description: 'High‑trust layout for local professionals.',
      caseStudy: 'Built to highlight credibility, reviews, and easy contact.',
      technologies: ['AI Launch', 'Branding', 'SEO Foundation'],
      results: {
        metric1: 'Clear trust signals',
        metric2: 'Stronger messaging',
        metric3: 'Better conversion flow'
      },
      image: portfolioHero,
      url: '/contact'
    }
  ];

  const filteredProjects = selectedCategory === 'All' 
    ? projects 
    : projects.filter(project => project.category === selectedCategory);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors">
      <SEOHead
        title="Portfolio - Successful AI-Powered Website Projects | NeuraSites AI"
        description="Discover our successful AI-powered website projects and case studies. See how we've helped businesses transform their online presence with measurable results."
        keywords="web development portfolio, website case studies, AI website examples, successful web projects, business website transformations"
        ogTitle="Our Success Stories - AI-Powered Website Portfolio"
        ogDescription="Discover how we've helped businesses transform their online presence with AI-powered websites that drive real results."
        canonicalUrl={`${siteUrl}/portfolio`}
        structuredData={structuredData}
      />
      
      <Header />
      
      {/* Hero Banner */}
      <section className="relative overflow-hidden brand-soft-surface">
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute inset-0 bg-center bg-cover"
            style={{ backgroundImage: `url(${portfolioHero})` }}
            aria-hidden="true"
          />
          <div className="absolute inset-0 brand-soft-overlay" />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6">
            Our Work:
            <span className="block brand-heading-accent">
              15 Years of Proven Expertise
            </span>
          </h1>
          <h2 className="mt-6 text-2xl md:text-3xl font-semibold text-gray-900 dark:text-white">
            AI is a powerful tool. The expert behind it is what matters.
          </h2>
          <div className="mt-8 space-y-6 max-w-4xl mx-auto text-center">
            <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300">Before founding NeuraSites AI, our principal developer spent 15 years building professional, custom websites by hand. This portfolio is the proof.</p>
            <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300">It showcases a long-standing commitment to clean code, beautiful design, and—most importantly—building sites that help real businesses grow.</p>
            <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300">Today, we use AI to deliver this exact same high-level quality, just faster and at a fraction of the cost. This is the 15 years of experience you get with every NeuraSites AI project.</p>
          </div>
        </div>
      </section>

      <section className="py-16 brand-soft-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">A Selection of Our Past Projects</h2>
                    <div className="mt-8 space-y-4 max-w-3xl mx-auto text-lg md:text-xl text-gray-600 dark:text-gray-300 text-left md:text-center">
            <p className="text-lg text-gray-600 dark:text-gray-200">
              Explore highlighted builds that demonstrate how that 15-year foundation translates into measurable wins for modern businesses.
            </p>
          </div>
        </div>
      </section>

      {/* Filter Tabs */}
      <section className="py-8 bg-white dark:bg-gray-900 sticky top-16 z-40 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full font-medium transition-all whitespace-nowrap ${
                  selectedCategory === category
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Grid */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {filteredProjects.map((project) => (
              <div key={project.id} className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all transform hover:-translate-y-1">
                <div className="relative group">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-64 object-cover transition-transform group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="absolute bottom-4 left-4 right-4">
                      <a
                        href={project.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-white text-gray-900 px-4 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center whitespace-nowrap"
                      >
                        View Live Site <i className="ri-external-link-line ml-2"></i>
                      </a>
                    </div>
                  </div>
                  <div className="absolute top-4 left-4">
                    <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                      {project.category}
                    </span>
                  </div>
                </div>
                
                <div className="p-8">
                  <div className="mb-4">
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{project.title}</h3>
                    <p className="text-blue-600 dark:text-blue-300 font-medium">{project.client}</p>
                  </div>
                  
                  <p className="text-gray-600 dark:text-white mb-6">{project.description}</p>
                  
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Case Study:</h4>
                    <p className="text-sm text-gray-600 dark:text-white leading-relaxed">{project.caseStudy}</p>
                  </div>

                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Technologies Used:</h4>
                    <div className="flex flex-wrap gap-2">
                      {project.technologies.map((tech, idx) => (
                        <span key={idx} className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-white px-3 py-1 rounded-full text-sm">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="border-t border-gray-200 dark:border-gray-600 pt-6">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Key Results:</h4>
                    <div className="grid grid-cols-1 gap-2">
                      <div className="flex items-center text-sm">
                        <i className="ri-arrow-up-line text-green-500 mr-2"></i>
                        <span className="text-gray-600 dark:text-white">{project.results.metric1}</span>
                      </div>
                      <div className="flex items-center text-sm">
                        <i className="ri-arrow-up-line text-green-500 mr-2"></i>
                        <span className="text-gray-600 dark:text-white">{project.results.metric2}</span>
                      </div>
                      <div className="flex items-center text-sm">
                        <i className="ri-arrow-up-line text-green-500 mr-2"></i>
                        <span className="text-gray-600 dark:text-white">{project.results.metric3}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 brand-cta-surface">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Let's Build Your Future Site</h2>
          <div className="mt-8 space-y-4 max-w-xl mx-auto">
            <p className="text-lg text-white mb-6">Your business deserves a professional, high-performance website built by a true expert. We're ready to make it happen.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/vibe-check"
              className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap"
            >
              Start Your Project
            </Link>
            <Link
              to="/pricing"
              className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-[#0F2A4E] transition-colors whitespace-nowrap"
            >
              See Our Packages
            </Link>
          </div>
          <div className="mt-8 flex flex-wrap justify-center items-center gap-8 text-sm text-white">
            <div className="flex items-center">
              <i className="ri-trophy-line text-yellow-500 mr-2"></i>
              100+ successful projects
            </div>
            <div className="flex items-center">
              <i className="ri-heart-line text-red-500 mr-2"></i>
              98% client satisfaction
            </div>
            <div className="flex items-center">
              <i className="ri-rocket-line text-cyan-500 mr-2"></i>
              Average 200% ROI increase
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
